import type { Icon } from '@phosphor-icons/react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { Mascot } from './Mascot'
import type { MascotVariant } from './Mascot'

export interface EmptyStateProps {
  title: string
  description: string
  icon: Icon
  actionLabel?: string
  onAction?: () => void
  actionSelector?: string
  actionDisabled?: boolean
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  secondaryActionSelector?: string
  filterActive?: boolean
  onClearFilter?: () => void
  fullScreen?: boolean
  className?: string
  mascot?: MascotVariant
  mascotSize?: 'sm' | 'md' | 'lg' | 'xl'
  /** 'sm' fits panel and card contexts where the default is too large. */
  size?: 'default' | 'sm'
}

export interface CompactEmptyStateProps {
  title?: string
  description?: string
  className?: string
}

/** Compact empty state for inline sections like sprint task lists. */
export function CompactEmptyState({
  title = 'No tasks yet',
  description = 'Tasks added to this section will appear here.',
  className,
}: CompactEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-4 py-8 text-center',
        'animate-in fade-in-0 fill-mode-forwards opacity-0 duration-300',
        className,
      )}
    >
      <Mascot variant="empty" size="sm" className="mb-3" />
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      <p className="mt-1 max-w-[250px] text-xs text-muted-foreground/70">
        {description}
      </p>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  icon: Icon,
  actionLabel,
  onAction,
  actionSelector,
  actionDisabled = false,
  secondaryActionLabel,
  onSecondaryAction,
  secondaryActionSelector,
  filterActive,
  onClearFilter,
  fullScreen = false,
  className,
  mascot,
  mascotSize = 'lg',
  size = 'default',
}: EmptyStateProps) {
  const isSmall = size === 'sm'

  const handleAction = () => {
    if (onAction) {
      onAction()
      return
    }

    if (actionSelector) {
      const element = document.querySelector(actionSelector)
      if (element instanceof HTMLElement) element.click()
    }
  }

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction()
      return
    }

    if (secondaryActionSelector) {
      const element = document.querySelector(secondaryActionSelector)
      if (element instanceof HTMLElement) element.click()
    }
  }

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col items-center justify-center px-5 text-center sm:px-8',
        'animate-in fade-in duration-300',
        fullScreen ? 'min-h-[80vh]' : isSmall ? 'py-9' : 'py-14',
        className,
      )}
    >
      <div
        className={cn(
          'flex w-full min-w-0 flex-col items-center',
          isSmall ? 'max-w-[28rem]' : 'max-w-[32rem]',
        )}
      >
        {mascot ? (
          <Mascot
            variant={mascot}
            size={mascotSize}
            className="mb-6 delay-100"
          />
        ) : (
          <div
            className={cn(
              'mx-auto flex shrink-0 items-center justify-center rounded-full',
              'border border-border/60 bg-background shadow-[0_1px_2px_rgb(0_0_0/0.04),0_8px_24px_-16px_rgb(0_0_0/0.28)]',
              'dark:bg-muted/40',
              isSmall ? 'h-11 w-11' : 'h-16 w-16',
            )}
          >
            <Icon
              weight="regular"
              className={
                isSmall ? 'h-5 w-5 text-primary' : 'h-7 w-7 text-primary'
              }
            />
          </div>
        )}

        <h3
          className={cn(
            'text-balance font-semibold leading-tight tracking-[-0.015em] text-foreground',
            isSmall ? 'mt-4 text-[15px]' : 'mt-5 text-xl',
          )}
        >
          {title}
        </h3>

        <p
          className={cn(
            'mx-auto max-w-[40ch] text-balance font-normal text-muted-foreground',
            isSmall ? 'mt-2 text-[13px] leading-5' : 'mt-3 text-sm leading-6',
          )}
        >
          {description}
        </p>

        <div
          className={cn(
            'flex w-full flex-col items-stretch justify-center gap-2.5 sm:w-auto sm:flex-row sm:items-center',
            isSmall ? 'mt-5' : 'mt-7',
          )}
        >
          {filterActive && onClearFilter && (
            <Button
              variant="outline"
              size={isSmall ? 'sm' : 'default'}
              onClick={onClearFilter}
              className="rounded-lg shadow-sm transition-[background-color,border-color,color,box-shadow] duration-150 hover:shadow"
            >
              Clear Filters
            </Button>
          )}

          {actionLabel && (onAction || actionSelector) && (
            <Button
              onClick={handleAction}
              disabled={actionDisabled}
              size={isSmall ? 'sm' : 'default'}
              className="rounded-lg shadow-sm transition-[background-color,color,box-shadow] duration-150 hover:shadow"
            >
              {actionLabel}
            </Button>
          )}

          {secondaryActionLabel &&
            (onSecondaryAction || secondaryActionSelector) && (
              <Button
                variant="outline"
                size={isSmall ? 'sm' : 'default'}
                onClick={handleSecondaryAction}
                className="rounded-lg shadow-sm transition-[background-color,border-color,color,box-shadow] duration-150 hover:shadow"
              >
                {secondaryActionLabel}
              </Button>
            )}
        </div>
      </div>
    </div>
  )
}
